import React, { useState } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { ProfessionalPaymentRecord } from '../../types/professional-payment';
import { Timestamp } from 'firebase/firestore';

interface DeletePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: ProfessionalPaymentRecord;
  onConfirm: () => Promise<void>;
  loading: boolean;
}

export function DeletePaymentModal({
  isOpen,
  onClose,
  payment,
  onConfirm,
  loading,
}: DeletePaymentModalProps) {
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen) return null;

  const formatDate = (date: Timestamp | Date) => {
    if (date instanceof Timestamp) {
      return date.toDate().toLocaleDateString('pt-BR');
    }
    return date.toLocaleDateString('pt-BR');
  };

  const isConfirmed = confirmText.toUpperCase() === 'CONFIRMAR';

  const handleConfirm = async () => {
    if (!isConfirmed) return;

    await onConfirm();
    setConfirmText('');
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Apagar Pagamento
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-semibold text-red-900">
                  ATENÇÃO: Esta ação é irreversível!
                </p>
                <p className="text-sm text-red-800">
                  Você está prestes a apagar permanentemente este registro de pagamento.
                  Todos os dados serão perdidos e não poderão ser recuperados.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">
              Dados do Pagamento a ser Apagado:
            </h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Profissional:</span>
                <span className="font-medium text-gray-900">
                  {payment.professionalName}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Período:</span>
                <span className="font-medium text-gray-900">
                  {payment.reportPeriod}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Sessões:</span>
                <span className="font-medium text-gray-900">
                  {payment.totalSessions}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Valor Total:</span>
                <span className="font-medium text-green-600">
                  R$ {payment.totalAmount.toFixed(2)}
                </span>
              </div>

              {payment.isPaid && payment.paidAt && (
                <>
                  <div className="border-t border-gray-200 my-2 pt-2"></div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-green-600">PAGO</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data do Pagamento:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(payment.paidAt)}
                    </span>
                  </div>
                  {payment.paymentMethod && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Método:</span>
                      <span className="font-medium text-gray-900">
                        {payment.paymentMethod}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Para confirmar a exclusão, digite <span className="font-bold text-red-600">CONFIRMAR</span>:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={loading}
              placeholder="Digite CONFIRMAR"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isConfirmed || loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Apagando...
              </>
            ) : (
              <>
                <Trash2 size={18} />
                Apagar Permanentemente
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
